import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, CircleMinus, CircleSlash, Clock } from "./icons";
import { Badge as BadgePrimitive } from "./primitives";
import { cn } from "./cn";

/**
 * IRN, e-way bill and Tally sync status — the three compliance fields §17 puts on one invoice row.
 *
 * ## The meaning is in the text, not in the colour (`006` R3)
 *
 * The printed invoice is A4 monochrome. A green/red pair prints as grey/grey, and a colour-only
 * status field silently stops existing on the document that actually leaves the building. So every
 * badge reads its subject **and** its state as words — "IRN registered", "E-way bill expired" —
 * with colour and icon as the second and third signal.
 *
 * This is also the WCAG requirement, which is usually how it goes: the accessibility fix and the
 * correctness fix are the same fix.
 *
 * ## Five states, and "not applicable" is one of them
 *
 * Collapsing any pair loses information a compliance user needs (`011` R6). In particular
 * `not_applicable` must not imply something is missing — below a configured threshold it is the
 * correct and complete answer (D-24, `011` FR-013), and it is the answer for most invoices.
 *
 * `pending` covers "in flight" **and** "the portal could not be reached". It is never rendered as
 * failed: that is `009`'s queued/failed distinction, and it means the same thing here (FR-016).
 */
export type ComplianceSubject = "irn" | "eway_bill" | "tally";

export type ComplianceState =
  /** Below a configured threshold, or out of scope. Nothing is missing. */
  | "not_applicable"
  /** In flight, or the far end could not be reached. Not a failure. */
  | "pending"
  /** The far end answered and accepted. */
  | "done"
  /** The far end answered and refused. */
  | "failed"
  /** Was valid, no longer is. E-way bill only, and distinct from both blank and failed. */
  | "expired";

const SUBJECT: Record<ComplianceSubject, string> = {
  irn: "IRN",
  eway_bill: "E-way bill",
  tally: "Tally",
};

/** "Accepted" is a different word for each subject, and the reader's vocabulary is the subject's. */
const DONE: Record<ComplianceSubject, string> = {
  irn: "registered",
  eway_bill: "generated",
  tally: "synced",
};

const STATE: Record<Exclude<ComplianceState, "done">, string> = {
  not_applicable: "not applicable",
  pending: "pending",
  failed: "failed",
  expired: "expired",
};

const ICON: Record<ComplianceState, ReactNode> = {
  not_applicable: <CircleMinus aria-hidden />,
  pending: <Clock aria-hidden />,
  done: <CheckCircle2 aria-hidden />,
  failed: <AlertTriangle aria-hidden />,
  expired: <CircleSlash aria-hidden />,
};

const TONE: Record<ComplianceState, string> = {
  not_applicable: "border-muted bg-gray-100 text-muted-foreground",
  pending: "border-orange/40 bg-orange/10 text-orange-dark",
  done: "border-green/40 bg-green/10 text-green-dark",
  failed: "border-red/40 bg-red/10 text-red-dark",
  expired: "border-red/40 bg-red/10 text-red-dark",
};

export function ComplianceStatusBadge({
  subject,
  state,
}: {
  subject: ComplianceSubject;
  state: ComplianceState;
}) {
  const word = state === "done" ? DONE[subject] : STATE[state];

  return (
    <BadgePrimitive
      variant="outline"
      data-slot="compliance-status-badge"
      data-subject={subject}
      data-state={state}
      className={cn("gap-1", TONE[state])}
    >
      {ICON[state]}
      {SUBJECT[subject]} {word}
    </BadgePrimitive>
  );
}
